import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { ChevronLeft } from 'lucide-react';
import PatientSelector from '../../../components/patients/PatientSelector';
import AddPatientModal from '../../../components/patients/AddPatientModal';
import { useDispatch } from 'react-redux';
import {
  resetTranscribeToPlanStatus,
  setSentToPatientsIds,
} from '../../../redux/slices/treatmentPlan';
import { useEffect, useMemo, useState } from 'react';
import PlanCreationOptions from './PlanCreationOptions';

export default function SelectPatient() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [showPlanOptions, setShowPlanOptions] = useState(false);
  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const [hasAppliedLocationState, setHasAppliedLocationState] = useState(false);

  const locationState = useMemo(
    () =>
      (location.state || {}) as {
        preselectedPatientId?: string;
        preselectedPatientName?: string;
      },
    [location.state]
  );

  const onSelectPatient = (patientId: string) => {
    dispatch(setSentToPatientsIds([patientId]));
    setShowPlanOptions(true);
  };

  useEffect(() => {
    if (!hasAppliedLocationState && locationState.preselectedPatientId) {
      dispatch(setSentToPatientsIds([locationState.preselectedPatientId]));
      setShowPlanOptions(true);
      setHasAppliedLocationState(true);
    }
  }, [dispatch, hasAppliedLocationState, locationState.preselectedPatientId]);

  const handlePlanOption = (option: 'ai' | 'manual' | 'template') => {
    if (option === 'ai') {
      dispatch(resetTranscribeToPlanStatus());
      navigate('/dashboard/treatment-plan/create-with-ai-options');
    } else if (option === 'manual') {
      navigate('/dashboard/treatment-plan');
    } else if (option === 'template') {
      navigate('/dashboard/templates');
    }
  };

  const handleAddPatient = () => {
    setShowAddPatientModal(true);
  };

  const handleAddPatientSuccess = () => {
    setShowAddPatientModal(false);
  };

  const handleAddPatientClose = () => {
    setShowAddPatientModal(false);
  };

  return (
    <>
      <Box sx={{ minHeight: '100vh', background: '#FAFBFC', p: '32px' }}>
        <Box py={'32px'} pt="0">
          <Button
            variant="text"
            color="primary"
            sx={{ minWidth: 100 }}
            startIcon={<ChevronLeft />}
            onClick={() => (!showPlanOptions ? navigate('/') : setShowPlanOptions(false))}
          >
            {showPlanOptions ? 'Select patient' : 'Dashboard'}
          </Button>
        </Box>
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          {!showPlanOptions ? (
            <PatientSelector
              mode="page"
              onAddPatient={handleAddPatient}
              onSkip={() => {
                setShowPlanOptions(true);
                dispatch(setSentToPatientsIds([]));
              }}
              onSelectPatient={onSelectPatient}
              showSkip={true}
            />
          ) : (
            <>
              {locationState.preselectedPatientName && (
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                  Creating plan for {locationState.preselectedPatientName}
                </Typography>
              )}
              <PlanCreationOptions
                onGenerateAI={() => handlePlanOption('ai')}
                onManual={() => handlePlanOption('manual')}
                onTemplate={() => handlePlanOption('template')}
              />
            </>
          )}
        </Box>
      </Box>

      <AddPatientModal
        open={showAddPatientModal}
        onClose={handleAddPatientClose}
        onSubmit={handleAddPatientSuccess}
      />
    </>
  );
}
